import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from '@mui/material';
import { Warning, CheckCircle, Cancel } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface IPreselectionConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    submissionTitle: string;
    action: 'select' | 'deselect';
    isEmailSent: boolean;
}

export const PreselectionConfirmationDialog: React.FC<IPreselectionConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    submissionTitle,
    action,
    isEmailSent,
}) => {
    const { themeStyle } = useThemeContext();

    const isSelecting = action === 'select';
    const actionText = isSelecting ? 'select' : 'deselect';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].deep_grey,
                    color: theme[themeStyle].tag_white,
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: `1px solid ${theme[themeStyle].tag_white}20`,
                }}>
                <Warning sx={{ color: '#ff9800', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: theme[themeStyle].tag_white }}>
                    Confirm {actionText.charAt(0).toUpperCase() + actionText.slice(1)} Submission
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="body1" sx={{ color: theme[themeStyle].tag_white }}>
                        Are you sure you want to <strong>{actionText}</strong> the submission:
                    </Typography>

                    <Box
                        sx={{
                            backgroundColor: `${theme[themeStyle].tag_white}10`,
                            border: `1px solid ${theme[themeStyle].tag_white}30`,
                            borderRadius: 2,
                            p: 2,
                        }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontWeight: 'bold',
                                mb: 1,
                            }}>
                            {submissionTitle}
                        </Typography>
                    </Box>

                    {isSelecting && (
                        <Alert
                            severity="info"
                            sx={{
                                backgroundColor: `${theme[themeStyle].primary_red}`,
                                color: theme[themeStyle].tag_white,
                                border: `1px solid ${theme[themeStyle].tag_white}30`,
                                '& .MuiAlert-icon': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}>
                            <Typography variant="body2">
                                <strong>Note:</strong> Once selected, this submission will be marked for review and may
                                receive a selection email.
                            </Typography>
                        </Alert>
                    )}

                    {!isSelecting && isEmailSent && (
                        <Alert
                            severity="warning"
                            sx={{
                                backgroundColor: `${theme[themeStyle].primary_red}`,
                                color: '#ff9800',
                                border: `1px solid #ff9800`,
                                '& .MuiAlert-icon': {
                                    color: '#ff9800',
                                },
                            }}>
                            <Typography variant="body2">
                                <strong>Warning:</strong> A selection email has already been sent for this submission.
                                Deselecting it may cause confusion for the creator.
                            </Typography>
                        </Alert>
                    )}

                    <Typography variant="body2" sx={{ color: `${theme[themeStyle].tag_white}80` }}>
                        This action will update the submission's selection status in the system.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    gap: 2,
                    borderTop: `1px solid ${theme[themeStyle].tag_white}20`,
                }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<Cancel />}
                    sx={{
                        color: theme[themeStyle].tag_white,
                        borderColor: theme[themeStyle].tag_white,
                        '&:hover': {
                            borderColor: theme[themeStyle].tag_white,
                            backgroundColor: `${theme[themeStyle].tag_white}20`,
                        },
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    startIcon={isSelecting ? <CheckCircle /> : <Cancel />}
                    color={isSelecting ? 'success' : 'warning'}
                    sx={{
                        backgroundColor: isSelecting ? '#4caf50' : '#ff9800',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: isSelecting ? '#45a049' : '#f57c00',
                        },
                    }}>
                    {isSelecting ? 'Select Submission' : 'Deselect Submission'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
